import React from "react";

import { TextField, TextFieldProps } from "@mui/material";
import { useController } from "react-hook-form";

import {
  BulletinFormField,
  BulletinFormValues,
} from "@/components/alerts/add-v2/model/types";

type BulletinTextFieldProps = Omit<
  TextFieldProps,
  "name" | "value" | "onChange" | "onBlur" | "error"
> & {
  name: BulletinFormField;
};

/**
 * A text or select input bound to one top-level field.
 *
 * `useController` registers the input, so it re-renders only when its own value changes and
 * react-hook-form can focus it. Fields that are not a single input — the autocompletes, the row
 * lists — go through `useBulletinField` instead.
 *
 * `null` renders as an empty string, and clearing the input writes `""` rather than `null`:
 * `model/adapters.ts` normalises empty strings on the way to the payload, so the form does not carry
 * two representations of "not filled in".
 *
 * The error comes from `fieldState`, so it appears only once react-hook-form has validated *this*
 * field — on the user's own change, or on the `trigger()` a blocked Finalize fires. A caller's own
 * `helperText` is what shows when there is no error, so a hint and an error never stack.
 */
export const BulletinTextField: React.FC<BulletinTextFieldProps> = ({
  name,
  children,
  helperText,
  ...textFieldProps
}) => {
  const { field, fieldState } = useController<BulletinFormValues>({ name });

  return (
    <TextField
      {...textFieldProps}
      name={field.name}
      inputRef={field.ref}
      value={(field.value as string | null) ?? ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={!!fieldState.error}
      helperText={fieldState.error?.message ?? helperText}
    >
      {children}
    </TextField>
  );
};
